import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DotsSixVertical as DragIcon } from "@phosphor-icons/react/DotsSixVertical";
import { DotsThree as MenuIcon } from "@phosphor-icons/react/DotsThree";
import { FC, Fragment, ReactNode } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";

import { usePopover } from "~ui-components/hooks/use-popover";
import { LayerGroup } from "~ui-components/types/__generated/gql/graphql";

import { LayerContextMenu } from "./layer-context-menu";

interface LayersComponentProps {
	layerGroup: LayerGroup;
	selectedLayerId?: string;
	onSelectLayer: (layerId: string) => void;
	onRemoveLayer: (layerId: string) => void;
	onLayerVisibilityChange: (id: string, visible: boolean) => void;
}

export const LayersComponent: FC<LayersComponentProps> = ({
	layerGroup,
	selectedLayerId,
	onSelectLayer,
	onRemoveLayer,
	onLayerVisibilityChange,
}) => {
	const popover = usePopover<HTMLButtonElement, string>();

	return (
		<>
			{/* @ts-ignore */}
			<Droppable
				direction="vertical"
				type="LAYER_GROUP"
				droppableId={layerGroup.id}>
				{(provided) => (
					<div
						{...provided.droppableProps}
						ref={provided.innerRef}
						style={{ display: "flex", flexDirection: "column" }}>
						{layerGroup.layerCollection.items.map((layer, index) => (
							<Fragment key={layer.id}>
								{/* @ts-ignore */}
								<Draggable
									index={index}
									draggableId={layer.id}>
									{(provided) => (
										<Card
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											sx={{
												m: 1,
												backgroundColor:
													selectedLayerId === layer.id
														? "var(--mui-palette-primary-selected)"
														: "var(--mui-palette-background-level2)",
											}}>
											<Stack
												direction="row"
												key={index}
												sx={{
													alignItems: "center",
													flexWrap: "wrap",
													justifyContent: "space-between",
													px: 1,
													py: 1,
												}}>
												<Stack
													direction="row"
													spacing={2}
													sx={{ alignItems: "center" }}>
													<IconButton size="small">
														<DragIcon size={20} />
													</IconButton>
													<Link
														onClick={() => onSelectLayer(layer.id)}
														color="CaptionText">
														<Typography variant="subtitle1">
															{layer.name}
														</Typography>
													</Link>
												</Stack>
												<Stack
													direction="row"
													spacing={2}
													sx={{ alignItems: "center" }}>
													<IconButton
														size="small"
														onClick={(e) => {
															popover.anchorRef.current = e.currentTarget;
															popover.handleOpen(layer.id);
														}}
														ref={popover.anchorRef}>
														<MenuIcon size={20} />
													</IconButton>
												</Stack>
											</Stack>
										</Card>
									)}
								</Draggable>
							</Fragment>
						))}
						{provided.placeholder as ReactNode}
					</div>
				)}
			</Droppable>
			<LayerContextMenu
				anchorEl={popover.anchorRef.current}
				onClose={popover.handleClose}
				open={popover.open}
				onDelete={() => {
					if (popover.data) onRemoveLayer(popover.data);
					popover.handleClose();
				}}
				onLayerVisibilityChange={onLayerVisibilityChange}
				currentLayer={layerGroup.layerCollection.items.find(
					(layer) => layer.id === popover.data,
				)}
			/>
		</>
	);
};
